import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	Box,
	Grid,
	Paper,
	Skeleton,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import { BarChart }  from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { FlagsIconSeeker } from '@ui-seeker/core'
import { normalizeCountryName, translateCountryName } from '@ui-seeker/utils'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { AppCardTitle, AppEmptyLine, AppPageTabs, AppPillToggle, AppNote } from '@components'
import { displayDay } from '@utils'
import { dsColorNew } from '@palette'
import { MbPage, MbKpiCard, MbKpiRow, MB_CHART_COLORS, MbPageSkeleton, mbSectionBarSx, mbLegendBottom, mbCompact } from 'Pages/MyBusiness/_shared/Ui'

import {
	FiltersBar,
	defaultFilters,
	filtersToQuery,
} from 'Pages/MyBusiness/_shared/Filters'
import { ShareList, BookingPaceCard, CancellationRateCard, CancellationByPartnerTable, ChannelMix, PartnerDrillDialog, ServiceTypeBreakdown, UpgradeAnalyticsTable, RevenueBucketsBanner, RevenueSourcesTable, PricelistBreakdown, buildTickFormatter, pickTickNumber } from 'Pages/MyBusiness/_shared/Charts'

/**
 * A1 — Revenue.
 *
 * KPI banner + Monthly chart (metric-switchable) + Office/Category
 * shares, with the project-wide filter baseline (date presets,
 * Office/Category slicers, PY/custom comparison).
 *
 * Backend: GET /api/my-business/revenue?from=&to=&offices=&sipps=&
 *                                       compare=&compare_from=&compare_to=
 *
 * Next sprints add: Booking Tracking + Cancellation Rate widgets, D3
 * channel mapping admin, and (separately) a Fleet dashboard.
 */
export default function MyBusinessRevenuePage() {
	// Two filter states:
	//   - applied: what the data on screen reflects (drives the fetch)
	//   - draft:   what the user is composing in the bar
	// Instant-intent events (presets, compare mode) sync both. Pending
	// events only update the draft and light the Apply button.
	const [applied, setApplied]           = useState(defaultFilters)
	const [draft,   setDraft]             = useState(defaultFilters)
	const [data,    setData]              = useState(null)
	const [error,   setError]             = useState(null)
	// A granularity switch only reloads the chart: the rest of the page stays
	const [chartReloading, setChartReloading] = useState(false)
	const [loading, setLoading]           = useState(true)
	const [activeMetricKey, setActiveMetricKey] = useState('revenue')
	// 'auto' = let backend pick by window length. User can override
	// from the chart toggle; reset to 'auto' on every Apply.
	const [granularity, setGranularity]   = useState('auto')
	// Visualization mode for the breakdowns (Thibault's 3 checkboxes):
	//   'raw'      → absolute values, no comparison
	//   'vs_py'    → absolute values + delta vs PY (default when compare=py)
	//   'per_unit' → absolute / avg_fleet (and PY / avg_fleet_py)
	const [viewMode, setViewMode]         = useState('vs_py')
	// Drill-down dialog anchor: { channelType?, sipp? } or null.
	const [drillAnchor, setDrillAnchor]   = useState(null)

	// Sub-tabs for the secondary panels. KPI banner + Monthly chart
	// stay above the tabs — they always reflect the global state so
	// clicking a slice in Breakdowns updates them without a tab switch.
	//
	// Local state instead of useSearchParams: changing the URL through
	// the router re-renders the whole tree and the browser scroll jumps
	// to the top. We sync the URL via history.replaceState (silent)
	// just so the deep-link still works on first load.
	const TABS = [
		{ key: 'breakdowns',   label: 'Breakdowns' },
		{ key: 'service_type', label: 'Service Type' },
		{ key: 'channels',     label: 'Channels' },
		{ key: 'health',       label: 'Health' },
	]
	const initialTab = (() => {
		if (typeof window === 'undefined') return TABS[0].key
		const t = new URLSearchParams(window.location.search).get('tab')
		return TABS.some(x => x.key === t) ? t : TABS[0].key
	})()
	const [activeTab, setActiveTab] = useState(initialTab)
	// The tabs differ a lot in height: keep the panel area as tall as the tallest one seen,
	// so switching to a short tab does not shrink the page and jump the scroll
	const panelsRef = useRef(null)
	const [panelsMinHeight, setPanelsMinHeight] = useState(0)
	const setTab = (key) => {
		const shown = panelsRef.current?.offsetHeight || 0
		setPanelsMinHeight(h => Math.max(h, shown))
		setActiveTab(key)
		if (typeof window !== 'undefined') {
			const url = new URL(window.location.href)
			url.searchParams.set('tab', key)
			window.history.replaceState(null, '', url)
		}
	}
	const activeMetric = METRICS[activeMetricKey] || METRICS.revenue

	const fetchRevenue = useCallback((f, g) => {
		setLoading(true); setError(null)
		let url = `/api/my-business/revenue?${filtersToQuery(f)}`
		if (g && g !== 'auto') url += `&granularity=${g}`
		fetch(url, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => { setLoading(false); setChartReloading(false) })
	}, [])

	const handleApply = useCallback((next) => {
		setApplied(next)
		setDraft(next)
		setGranularity('auto')
		fetchRevenue(next, 'auto')
	}, [fetchRevenue])

	const handleGranularity = (g) => {
		setChartReloading(true)
		setGranularity(g)
		fetchRevenue(applied, g)
	}

	const pageLoading = loading && !chartReloading

	useEffect(() => { fetchRevenue(applied, 'auto') }, [])  // initial only

	// Cross-filtering: highlight reflects an "isolate-one" applied
	// filter. If the slicer multi-selects N>1 offices the highlight
	// goes away (no single row to point at).
	const selectedOffice =
		applied.offices.length === 1 ? applied.offices[0] : null
	const selectedCategory =
		applied.categories.length === 1 ? applied.categories[0] : null

	const isolateOffice = (code) => {
		handleApply({
			...applied,
			offices: code ? [code] : [],
		})
	}
	const isolateCategory = (sipp) => {
		handleApply({
			...applied,
			categories: sipp ? [sipp] : [],
		})
	}

	// Build human-readable chips describing the active applied filters.
	// Looking names up against the breakdown rows already in `data`
	// avoids a separate catalogue fetch.
	const allFilterChips = useMemo(() => {
		const out = []
		const offs = applied.offices
		const cats = applied.categories
		if (offs.length === 1) {
			const o = data?.by_office?.find(x => x.code === offs[0])
			out.push({ kind: 'office',
			           label: `Office: ${offs[0]}${o?.name ? ` · ${o.name}` : ''}` })
		} else if (offs.length > 1) {
			out.push({ kind: 'office', label: `Offices: ${offs.length} selected` })
		}
		if (cats.length === 1) {
			const c = data?.by_category?.find(x => x.sipp_code === cats[0])
			out.push({ kind: 'category',
			           label: `Category: ${cats[0]}${c?.vehicle_class ? ` · ${c.vehicle_class}` : ''}` })
		} else if (cats.length > 1) {
			out.push({ kind: 'category', label: `Categories: ${cats.length} selected` })
		}
		return out
	}, [applied, data])

	const officeShareChips   = allFilterChips.filter(c => c.kind !== 'office')
	const categoryShareChips = allFilterChips.filter(c => c.kind !== 'category')

	return (
		<MbPage title={MY_BUSINESS_ROUTES.REVENUE_TITLE}
		        subtitle={MY_BUSINESS_ROUTES.REVENUE_SUBTITLE}>
			<Box>
				<FiltersBar value={draft}
				            applied={applied}
				            onChange={setDraft}
				            onApply={handleApply}
				            loading={loading}
				            exportHref={`/api/my-business/revenue/export?${filtersToQuery(applied)}`}
				            csvHref={`/api/my-business/revenue/download?${filtersToQuery(applied)}`}
				            csvLabel="Bookings CSV"/>


				{error && <AppNote severity="error" sx={{ mb: 2 }}>Backend unreachable: {error}</AppNote>}

				{pageLoading && <MbPageSkeleton kpis={5} tiles={5} blocks={['chart', 'tabs', 'split']}/>}

				{data && !pageLoading && (
					<Box>
						{/* Global state — KPI banner + Monthly chart. Always
						    visible so a slice click in Breakdowns updates
						    them without a tab switch. */}
						<KpiBanner kpi={data.kpi}
						           compareKpi={data.compare?.kpi}
						           yoy={data.compare?.yoy}
						           activeMetricKey={activeMetricKey}
						           onSelectMetric={setActiveMetricKey}/>
						<Box sx={{ mt: 2 }}>
							<RevenueBucketsBanner from={applied.date.from}
							                      to={applied.date.to}
							                      offices={applied.offices}
							                      categories={applied.categories}/>
						</Box>
						<Box sx={{ mt: 3 }}>
							<MonthlyChart reloading={chartReloading}
							            monthly={data.monthly}
							              compareMonthly={data.compare?.monthly}
							              compareLabel={data.compare && describeCompare(data.compare)}
							              metric={activeMetric}
							              granularity={data.granularity}
							              onGranularityChange={handleGranularity}/>
						</Box>

						{/* Secondary panels under their own tab strip. */}
						{/* The section tabs and, on Breakdowns, the metric line and the View toggle, in one white strip */}
						<Box sx={{ mt: 3, mb: 2, ...mbSectionBarSx, rowGap: 1 }}>
							<AppPageTabs value={activeTab}
							             onChange={(_, k) => setTab(k)}
							             tabs={TABS.map(t => ({ key: t.key, value: t.key, label: t.label }))}
							             stackProps={{ sx: { mb: 0 } }}/>
							{activeTab === 'breakdowns' && <>
								<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>
									Metric: <strong>{activeMetric.label}</strong> · pick another in the Overview banner
								</Typography>
								<Box display="flex" alignItems="center" gap={1.5} sx={{ ml: 'auto' }}>
									<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
										View
									</Typography>
									<ToggleButtonGroup size="small" exclusive value={viewMode}
									                   onChange={(_, v) => v && setViewMode(v)}>
										<ToggleButton value="raw">Raw</ToggleButton>
										<ToggleButton value="vs_py" disabled={!data.compare}>vs PY</ToggleButton>
										<ToggleButton value="per_unit" disabled={!data.compare}>per Unit</ToggleButton>
									</ToggleButtonGroup>
								</Box>
							</>}
						</Box>

						<Box ref={panelsRef} sx={{ minHeight: panelsMinHeight }}>
						<Box sx={{ display: activeTab === 'breakdowns' ? 'block' : 'none' }}>
								<Grid container spacing={2} sx={{ mt: 0 }}>
									<Grid item xs={12} md={6}>
										<ShareList title={`Share by Office · ${activeMetric.label}`}
										           rows={applyViewMode(data.by_office, viewMode, data, activeMetric.key)}
										           labelOf={(r) => r.code}
										           subOf={(r)   => r.name || ''}
										           valueFormatter={activeMetric.format}
										           valueKey={activeMetric.key}
										           keyField="code"
										           selectedKey={selectedOffice}
										           onSelect={isolateOffice}/>
									</Grid>
									<Grid item xs={12} md={6}>
										<ShareList title={`Share by Category (SIPP) · ${activeMetric.label}`}
										           rows={applyViewMode(data.by_category, viewMode, data, activeMetric.key)}
										           labelOf={(r) => r.sipp_code}
										           subOf={(r)   => r.vehicle_class || ''}
										           valueFormatter={activeMetric.format}
										           valueKey={activeMetric.key}
										           keyField="sipp_code"
										           selectedKey={selectedCategory}
										           onSelect={isolateCategory}/>
									</Grid>
								</Grid>
								{data.by_channel?.length > 0 && (
									<Box sx={{ mt: 3 }}>
										<ChannelMix title={`Share by Channel · ${activeMetric.label}`}
										            rows={applyViewMode(data.by_channel, viewMode, data, activeMetric.key)}
										            valueKey={activeMetric.key}
										            valueFormatter={activeMetric.format}
										            onChannel={(t) => t && setDrillAnchor({ channelType: t })}/>
									</Box>
								)}
								{data.by_country?.length > 0 && (
									<Box sx={{ mt: 3 }}>
										<ShareList title={`Share by Country · ${activeMetric.label}`}
										           rows={applyViewMode(data.by_country, viewMode, data, activeMetric.key)}
										           labelOf={(r) => (
											           <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
												           <FlagsIconSeeker countryName={r.country_code}/>{r.country_code}
											           </Box>
										           )}
										           subOf={(r)   => countryNameOf(r.country_code)}
										           valueFormatter={activeMetric.format}
										           valueKey={activeMetric.key}
										           keyField="country_code"/>
									</Box>
								)}
								<Box sx={{ mt: 3 }}>
									<UpgradeAnalyticsTable from={applied.date.from}
									                       to={applied.date.to}
									                       offices={applied.offices}
									                       categories={applied.categories}
									                       channels={applied.channels}
									                       partners={applied.partners}
									                       duration={applied.duration || 'total'}/>
								</Box>
								<PartnerDrillDialog anchor={drillAnchor}
								                    filters={applied}
								                    onClose={() => setDrillAnchor(null)}/>
							</Box>

							<Box sx={{ display: activeTab === 'service_type' ? 'block' : 'none' }}>
								<ServiceTypeBreakdown from={applied.date.from}
								                      to={applied.date.to}
								                      offices={applied.offices}
								                      categories={applied.categories}
								                      duration={applied.duration || 'total'}/>
								<Box sx={{ mt: 3 }}>
									<RevenueSourcesTable from={applied.date.from}
									                     to={applied.date.to}
									                     offices={applied.offices}
									                     categories={applied.categories}
									                     compare={applied.compare?.mode || 'py'}/>
								</Box>
							</Box>

							<Box sx={{ display: activeTab === 'channels' ? 'block' : 'none' }}>
								<PricelistBreakdown from={applied.date.from}
								                    to={applied.date.to}
								                    offices={applied.offices}
								                    categories={applied.categories}
								                    compare={applied.compare?.mode || 'py'}/>
							</Box>

							<Box sx={{ display: activeTab === 'health' ? 'block' : 'none' }}>
								<BookingPaceCard from={applied.date.from}
								                 to={applied.date.to}
								                 offices={applied.offices}
								                 categories={applied.categories}
								                 duration={applied.duration || 'total'}/>
								<Box sx={{ mt: 3 }}>
									<CancellationRateCard from={applied.date.from}
									                      to={applied.date.to}
									                      offices={applied.offices}
									                      categories={applied.categories}
									                      compare={applied.compare?.mode || 'py'}
									                      duration={applied.duration || 'total'}/>
								</Box>
								<Box sx={{ mt: 3 }}>
									<CancellationByPartnerTable from={applied.date.from}
									                            to={applied.date.to}
									                            offices={applied.offices}
									                            categories={applied.categories}
									                            duration={applied.duration || 'total'}/>
								</Box>
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		</MbPage>
	)
}

// Full country name for the share rows; codes the app has no flag for keep only the code
const countryNameOf = (code) => normalizeCountryName(code) ? translateCountryName(code) : ''

function describeCompare(c) {
	if (c.mode === 'py') return 'Previous Year'
	return `${displayDay(c.from)} → ${displayDay(c.to)}`
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
const fmtEur = (n) =>
	new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
		.format(n)
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)

/**
 * Active metric catalogue.
 *
 * Each KPI card in the banner is a switcher: click it and Monthly chart
 * + Shares (by Office, by Category) pivot to that metric. The card
 * provides the metric key; the catalogue maps key → display config.
 *
 * LoR is intentionally excluded — it's an average, not aggregatable as
 * a single bar across months or as a "share of total".
 */
const METRICS = {
	revenue:     { key: 'revenue',     label: 'Revenue (gross)', format: fmtEur, chartTitle: 'Revenue (gross)' },
	revenue_net: { key: 'revenue_net', label: 'Revenue (net)',   format: fmtEur, chartTitle: 'Revenue (net)' },
	bookings:    { key: 'bookings',    label: 'Bookings',        format: fmtNum, chartTitle: 'Bookings' },
	rental_days: { key: 'rental_days', label: 'Rental Days',     format: fmtNum, chartTitle: 'Rental Days' },
	rpd:         { key: 'rpd',         label: 'RPD',             format: fmtEur, chartTitle: 'RPD' },
}

// ---------------------------------------------------------------------------
// KPI banner
// ---------------------------------------------------------------------------
function KpiBanner({ kpi, compareKpi, yoy, activeMetricKey, onSelectMetric }) {
	const cards = [
		{ key: 'revenue',     label: 'Revenue (gross)', format: fmtEur, hint: 'with IGIC',    icon: 'sack-dollar' },
		{ key: 'revenue_net', label: 'Revenue (net)',   format: fmtEur, hint: 'without IGIC', icon: 'receipt' },
		{ key: 'bookings',    label: 'Bookings',        format: fmtNum,                       icon: 'calendar-check' },
		{ key: 'rental_days', label: 'Rental Days',     format: fmtNum,                       icon: 'calendar-days' },
		{ key: 'rpd',         label: 'RPD',             format: fmtEur, hint: `LoR ${kpi.lor}d`, icon: 'euro-sign' },
	]
	return (
		<MbKpiRow>
			{cards.map((c) => (
				<MbKpiCard key={c.key}
				           icon={c.icon}
				           label={c.label}
				           value={c.format(kpi[c.key])}
				           compareValue={compareKpi ? c.format(compareKpi[c.key]) : null}
				           delta={compareKpi ? (yoy ? yoy[c.key] : null) : undefined}
				           hint={c.hint}
				           selected={c.key === activeMetricKey}
				           onSelect={() => onSelectMetric?.(c.key)}/>
			))}
		</MbKpiRow>
	)
}

// ---------------------------------------------------------------------------
// Time-series chart — granularity (daily / weekly / monthly) toggleable
// ---------------------------------------------------------------------------
const GRANULARITY_LABEL = {
	daily:   'Daily',
	weekly:  'Weekly',
	monthly: 'Monthly',
}
const GRANULARITY_OPTIONS = Object.entries(GRANULARITY_LABEL).map(([value, label]) => ({ value, label }))

function MonthlyChart({ reloading, monthly, compareMonthly, compareLabel, metric,
                       granularity, onGranularityChange }) {
	if (!monthly.length) {
		return (
			<Paper sx={{ p: 3 }}>
				<AppEmptyLine>No data in this date range.</AppEmptyLine>
			</Paper>
		)
	}

	const granLabel = GRANULARITY_LABEL[granularity] || 'Monthly'

	// Adaptive tick format: shows year when the window crosses a year
	// boundary, hides it otherwise. Also caps tick count so labels
	// stop overlapping on long ranges.
	const periods = monthly.map(m => m.month)
	const tickFormat = buildTickFormatter(periods)
	const tickNumber = pickTickNumber(periods)

	// Align comparison by index — same length window expected from backend.
	const months = monthly.map(m => m.month)
	const values = monthly.map(m => m[metric.key] || 0)
	const series = [{ data: values, label: metric.chartTitle, valueFormatter: metric.format }]
	if (compareMonthly && compareMonthly.length) {
		const cmpValues = compareMonthly.map(m => m[metric.key] || 0)
		while (cmpValues.length < months.length) cmpValues.push(0)
		series.push({ data: cmpValues.slice(0, months.length),
		              label: compareLabel || 'Compare',
		              valueFormatter: metric.format })
	}

	return (
		<Paper sx={{ p: 2.5 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
				<AppCardTitle icon="chart-column" title={`${granLabel} ${metric.chartTitle}`}/>
				<AppPillToggle size="small" value={granularity || 'monthly'} onChange={(v) => onGranularityChange?.(v)} options={GRANULARITY_OPTIONS}/>
			</Box>
			{/* Daily = ~150+ data points → bars become a barcode. Use a
			    line chart for daily, bars for weekly/monthly where each
			    bucket is large enough to read at a glance. */}
			{/* a reload swaps only the chart for the skeleton's chart block: the title and the toggle stay */}
			{reloading ? <Skeleton variant="rounded" height={340} sx={{ borderRadius: '10px' }}/> : granularity === 'daily' ? (
				<LineChart
					colors={MB_CHART_COLORS}
					xAxis={[{ scaleType: 'point', data: months, valueFormatter: tickFormat, tickNumber }]}
					series={series.map((s, i) => ({
						...s, showMark: false, curve: 'monotoneX',
						area: i === 0,  // only the primary series gets fill
					}))}
					height={340}
					yAxis={[{ valueFormatter: mbCompact }]}
					slotProps={mbLegendBottom}
					margin={{ top: 16, right: 16, bottom: 72, left: 56 }}/>
			) : (
				<BarChart
					colors={MB_CHART_COLORS}
					xAxis={[{ scaleType: 'band', data: months, valueFormatter: tickFormat, tickNumber }]}
					series={series}
					height={340}
					yAxis={[{ valueFormatter: mbCompact }]}
					slotProps={mbLegendBottom}
					margin={{ top: 16, right: 16, bottom: 72, left: 56 }}/>
			)}
		</Paper>
	)
}


// ---------------------------------------------------------------------------
// View-mode transform for the breakdown shares (Thibault's 3 checkboxes).
// ---------------------------------------------------------------------------
// 'raw'      → strip the compare so deltas disappear.
// 'vs_py'    → leave rows untouched (default behaviour).
// 'per_unit' → scale absolute metrics by avg_fleet (current period) and
//              the comparison values by avg_fleet of the PY/custom window.
//              RPD is intrinsically a per-day rate; do not rescale it.
// ---------------------------------------------------------------------------
function applyViewMode(rows, mode, data, metricKey) {
	if (!rows) return rows
	if (mode === 'raw') {
		return rows.map(r => ({
			...r,
			revenue_compare: undefined,
			delta_pct: null,
		}))
	}
	if (mode === 'per_unit') {
		const avg   = Math.max(Number(data?.kpi?.avg_fleet) || 0, 1)
		const avgPy = Math.max(Number(data?.compare?.kpi?.avg_fleet) || 0, 1)
		return rows.map(r => {
			const scale = (k) => (k === 'rpd' ? r[k] : (r[k] || 0) / avg)
			const out = { ...r,
				revenue:     scale('revenue'),
				revenue_net: scale('revenue_net'),
				rental_days: scale('rental_days'),
				bookings:    scale('bookings'),
			}
			if (r.revenue_compare != null) {
				out.revenue_compare =
					metricKey === 'rpd' ? r.revenue_compare : (r.revenue_compare || 0) / avgPy
				const cur = out[metricKey] || 0
				const prev = out.revenue_compare || 0
				out.delta_pct = prev ? Number(((cur - prev) / prev * 100).toFixed(1)) : null
			}
			return out
		})
	}
	return rows  // 'vs_py' or anything else falls back to identity
}
